// taxSchemas.js
export const PTF_SCHEMA = {
  Tax_Basics: [
    {
      section: "Your_tax_details",
      infoTooltip:
        "Tell us about your tax year, where you earned income, and your filing status.",
      fields: [
        {
          name: "taxYears",
          label: "Tax year",
          description: "Choose all that apply",
          type: "multi_select",
          option: "taxableYears",
          required: true,
        },
        {
          name: "incomeStates",
          label: "States where you earned income",
          description: "Please select at least one state.",
          type: "multi_select",
          option: "usaStates",
          required: true,
        },
        {
          name: "filingStatus",
          label: "Filing status",
          type: "single_select",
          option: "filingStatuses",
          required: true,
        },
        {
          name: "haveDependents",
          label: "Do you have dependents?",
          type: "multi_boolean_choice",
          fields: [
            {
              type: "numeric",
              name: "dependentsCount",
              label: "Number of dependents",
              inputType: "integer",
              defaultValue: 1,
              description: "Enter the total number of dependents you plan to claim.",
            },
          ],
        },
      ],
    },
    {
      section: "Income_sources",
      infoTooltip:
        "Tell us what types of income you received this year — like W-2 jobs, self-employment, investments, or other earnings.",
      validation: { atLeastOneTrue: true },
      fields: [
        {
          name: "wtwoForm",
          label: "W-2 income",
          type: "multi_boolean_choice",
          fields: [
            {
              type: "numeric",
              name: "w2Count",
              label: "Number of W-2s",
              inputType: "integer",
              defaultValue: 1,
              description: "Income from an employer. You'll receive a W-2 for each job you worked."
            },
          ],
        },
        {
          name: "tenForm",
          label: "Self-employment income",
          description:
            "Income from freelance, contract, or business work. This is usually reported on one or more 1099-NEC forms.",
          type: "multi_boolean_choice",
          fields: [
            {
              type: "numeric",
              name: "ten99Count",
              label: "Number of 1099-NECs",
              inputType: "integer",
              defaultValue: 1,
            },
          ],
        },
        {
          name: "miscForm",
          label: "Miscellaneous income",
          description:
            "Income such as rent, prizes, awards, or other earnings not from a job. This is usually reported on 1099-MISC forms.",
          type: "multi_boolean_choice",
          fields: [
            {
              type: "numeric",
              name: "ten99Count",
              label: "Number of 1099-MISCs",
              inputType: "integer",
              defaultValue: 1,
            },
          ],
        },
        {
          name: "rentalProperties",
          label: "Rental property",
          type: "multi_boolean_choice",
          fields: [
            {
              type: "numeric",
              name: "rentalPropertiesCount",
              label: "Number of rental properties",
              inputType: "integer",
              defaultValue: 1,
              description: "Tell us how many total rental properties you own.",
            },
            {
              type: "multi_property",
              name: "properties",
              propertyFields: [
                {
                  name: "isResidential",
                  label: "Is Residential?",
                  type: "single_boolean_choice",
                },
                {
                  name: "isShortTermRental",
                  label: "Is Short-Term Rent (Airbnb)?",
                  type: "single_boolean_choice",
                },
                {
                  name: "isNewlyPurchased",
                  label: "Is Newly Purchased?",
                  type: "single_boolean_choice",
                },
              ],
            },
          ],
        },
        {
          name: "stockOrDividends",
          label: "Investment or brokerage accounts",
          description: "E.g: Fidelity, Robinhood, etc.",
          type: "multi_boolean_choice",
          fields: [
            {
              type: "numeric",
              name: "stockOrDividendsCount",
              label: "Number of statements",
              inputType: "integer",
              defaultValue: 1,
              description:
                "A statement is one tax document from a broker or investment provider. It may include stock sales, dividends, and interest, but counts as one statement per provider.",
            },
          ],
        },
        {
          name: "scheduleK",
          label: "Schedule K-1 income",
          type: "multi_boolean_choice",
          fields: [
            {
              type: "numeric",
              name: "scheduleKCount",
              label: "Number of K-1s",
              inputType: "integer",
              defaultValue: 1,
              description:
                "Enter how many K-1 forms you received. This may include partnership income (Form 1065 K-1), S-Corp income (Form 1120-S K-1), or income from trusts and estates (Form 1041).",
            },
          ],
        },
        {
          name: "cryptoTransaction",
          label: "Crypto",
          type: "multi_boolean_choice",
          description:
            "We currently support Coinbase (via 1099 forms) and Kraken (via transaction CSVs) to generate your crypto gains and losses using FIFO or LIFO cost-basis methods.",
          fields: [
            {
              type: "multi_select",
              name: "exchanges",
              label: "Select Exchange(s)",
              description:
                "Select the exchange(s) you use to buy or sell cryptocurrency.",
              option: "cryptocurrencyExchanges",
            },
            {
              type: "numeric",
              name: "coinbase1099Count",
              label: "Number of Coinbase 1099 forms",
              inputType: "integer",
              defaultValue: 1,
              min: 1,
              description:
                "Enter how many 1099 forms you received from Coinbase. Some users may receive multiple 1099 forms (e.g., 1099-B, 1099-MISC).",
              conditionalDisplay: {
                field: "exchanges",
                operator: "includes",
                value: "coinbase"
              }
            },
          ],
        },
        {
          name: "llc_owner",
          label: "Business or Single-Member LLC?",
          type: "multi_boolean_choice",
          description:
            "A Sole Proprietorship or Single-Member LLC is a business you own alone. Income and expenses flow through to your personal return (Schedule C).",
          fields: [
            {
              type: "radio_boolean_choice",
              name: "documentationType",
              options: [
                {
                  value: "profit_loss",
                  label: "I have a Profit & Loss summary document",
                  description:
                    "Select this if you have a complete Profit & Loss (P&L) summary document prepared for this business.",
                },
                {
                  value: "invoices",
                  label: "I only have receipts and invoices",
                  description:
                    "Select this if you only have individual receipts and invoices for each transaction. Processing time may be longer.",
                },
              ],
            },
          ],
        },
      ],
    },
    {
      section: "Health_insurance",
      infoTooltip:
        "Tell us about your health coverage for the year — this can affect certain credits and deductions.",
      required: false,
      fields: [
        {
          name: "healthInsurance",
          label: "Did You Have Health Insurance All Year?",
          type: "radio_boolean_choice",
          options: [
            {
              value: "yes",
              label: "Yes",
              fields: [
                {
                  type: "single_boolean_choice",
                  name: "marketplace",
                  label: "Marketplace insurance",
                  description:
                    "Select this if you had Marketplace (ACA) coverage and received Form 1095-A.",
                },
              ],
            },
            {
              value: "no",
              label: "No",
              fields: [
                {
                  type: "single_boolean_choice",
                  name: "exemptionsOrStateCoverage",
                  label: "Health coverage exemptions or state requirements",
                  description:
                    "Select this if you qualify for a federal exemption or if your state requires proof of minimum essential coverage. Examples include short gaps in coverage, income below the filing threshold, or state mandates in CA, MA, NJ, RI, and DC.",
                },
              ],
            },
          ],
        },
      ],
    },
    {
      section: "Credits_&_deductions",
      infoTooltip:
        "Tell us if any of these apply to you — things like tuition, student loan interest, retirement savings, childcare, or major medical expenses. They can reduce your taxable income or increase your refund.",
      fields: [
        {
          name: "homeOwnership",
          label: "Homeownership (Mortgage Interest – Form 1098)",
          type: "multi_boolean_choice",
          description:
            "Did your mortgage lender send you a Form 1098 showing mortgage interest paid?",
          fields: [
            {
              type: "numeric",
              name: "homeOwnershipCount",
              label: "How many 1098 forms do you want to report?",
              inputType: "integer",
              defaultValue: 1,
              description:
                "Enter the number of 1098 forms. You should receive one from each mortgage lender.",
            },
          ],
        },
        {
          name: "esLoans",
          label: "Education & Student Loans",
          type: "multi_boolean_choice",
          fields: [
            {
              type: "single_boolean_choice",
              name: "paidTuition",
              label: "Tuition payments",
              description: "Select this if you or a dependent paid qualified tuition and received a Form 1098-T.",
            },
            {
              type: "single_boolean_choice",
              name: "paidStudentLoanInterest",
              label: "Student loan interest",
              description: "Select this if you paid interest on a qualified student loan and received a Form 1098-E.",
            },
          ],
        },
        {
          name: "rcDistributions",
          label: "Retirement contributions or distributions",
          type: "multi_boolean_choice",
          fields: [
            {
              type: "single_boolean_choice",
              name: "contributionToIraRothSep",
              label: "Retirement contributions (Form 5498)",
              description: "Select this if you contributed to a traditional IRA, Roth IRA, or SEP IRA and received a Form 5498.",
            },
            {
              type: "single_boolean_choice",
              name: "distributionsFromRetirementAccounts",
              label: " Retirement distributions (Form 1099-R)",
              description: "Select this if you withdrew money from a pension, 401(k), IRA, or other retirement account and received a Form 1099-R.",
            },
          ],
        },
        {
          name: "cdcExpenses",
          label: "Childcare / Dependent Care Expenses?",
          type: "multi_boolean_choice",
          fields: [
            {
              type: "numeric",
              name: "expensesTotalAmount",
              label: "Enter your total childcare or dependent care expenses for the year.",
              defaultValue: 1,
            },
          ],
        },
        {
          name: "cdlmExpenses",
          label: "Charitable donations or large medical expenses",
          type: "multi_boolean_choice",
          fields: [
            {
              type: "numeric",
              name: "charitableDonationAmount",
              label: "Enter the total amount you donated to qualified charities.",
            },
            {
              type: "numeric",
              name: "largeMedicalExpensesAmount",
              label: "Enter your out-of-pocket medical expenses that were not reimbursed by insurance.",
            },
          ],
        },
      ],
    },
    {
      section: "Other_Situations",
      infoTooltip:
        "Tell us if any of these special situations apply to you — things like owning an LLC, selling property, or having foreign income. They can affect your tax filing and possible deductions.",
      fields: [
        {
          name: "propertiesAssets",
          label: "Did you sell any property or assets?",
          type: "multi_boolean_choice",
          description:
            "Include real estate, vehicles, collectibles, stocks, or any other assets you sold during the year. This helps your tax expert estimate the work required.",
          fields: [
            {
              type: "numeric",
              name: "realEstateSoldCount",
              label: "Number of Real Estate properties sold",
              inputType: "integer",
              description:
                "Include any homes, rental properties, or other real estate you sold this year. You'll need to provide documents like 1099-S, closing statements (HUD-1), and purchase information if applicable.",
            },
            {
              type: "numeric",
              name: "otherAssetsSoldCount",
              label: "Number of Other assets sold",
              inputType: "integer",
              description:
                "Include vehicles, collectibles, business assets, or personal property you sold this year. You'll need to provide documentation showing the sale price and purchase price or basis.",
            },
          ],
        },
        {
          name: "naturalDisaster",
          label: "Were you affected by a natural disaster?",
          type: "multi_boolean_choice",
          description:
            "Include events like hurricanes, floods, wildfires, tornadoes, or other federally declared disasters. This helps your tax expert determine if you qualify for casualty loss deductions",
          fields: [
            {
              type: "numeric",
              name: "naturalDisasterCount",
              label: "How many loss events do you want to report?",
              inputType: "integer",
              defaultValue: 1,
              description:
                "Enter the number of natural disaster loss events. For each, provide supporting documents such as insurance claims, repair receipts, or FEMA declarations.",
            },
          ],
        },
        {
          name: "foreignIncome",
          label: "Any foreign income, account or assets?",
          type: "multi_boolean_choice",
          description:
            "Include income earned outside the U.S., foreign bank or brokerage accounts, foreign trusts, foreign retirement accounts, or other foreign assets. This helps your tax expert determine if reporting (e.g., FBAR, Form 8938) is required.",
          fields: [
            {
              type: "numeric",
              name: "foreignBankAccountsCount",
              label: "Number of Foreign bank accounts",
              inputType: "integer",
              description:
                "Enter how many foreign bank accounts you own. For each, you’ll need to provide account statements showing balances and interest earned for the tax year.",
            },
            {
              type: "numeric",
              name: "foreignInvestmentAccountsCount",
              label: "Number of foreign investment or brokerage accounts",
              inputType: "integer",
              description:
                "Enter how many foreign investment or brokerage accounts you own. For each, provide statements showing transactions, dividends, and interest earned.",
            },
            {
              type: "numeric",
              name: "foreignRetirementAccountsCount",
              label: "Number of foreign retirement accounts",
              inputType: "integer",
              description:
                "Enter how many foreign retirement accounts you own. For each, provide annual statements including contributions, distributions, and income earned.",
            },
            {
              type: "numeric",
              name: "foreignRealEstatePropertiesCount",
              label: "Number of foreign real estate properties",
              inputType: "integer",
              description:
                "Enter how many foreign properties you own. For each, provide purchase documents, rental income and expense statements, or sale documents if sold.",
            },
            {
              type: "numeric",
              name: "foreignOtherAssetsCount",
              label: "Number of other foreign assets",
              inputType: "integer",
              description:
                "Enter how many other foreign assets you own, such as collectibles or business interests. For each, provide documentation showing ownership, value, and any income received.",
            },
          ],
        },
      ],
    },
    {
      section: "Additional_Notes",
      infoTooltip:
        "Add anything else you think your tax professional should know — extra details, explanations, or special circumstances.",
      fields: [
        {
          name: "description",
          label: "Anything else to add?",
          placeholder: "Anything else to add",
          type: "textarea",
        },
      ],
    },
  ],
  Background: [
    {
      section: "Personal_Information",
      fields: [
        {
          name: "firstname",
          label: "First Name",
          placeholder: "",
          type: "input",
          required: true,
          input_type: "text",
        },
        {
          name: "lastname",
          label: "Last Name",
          placeholder: "",
          type: "input",
          required: true,
          input_type: "text",
        },
        {
          name: "email",
          label: "Email address",
          placeholder: "",
          type: "input",
          required: true,
          input_type: "email",
        },
        {
          name: "phone",
          label: "Phone number",
          placeholder: "(###)-###-####",
          type: "input",
          required: true,
          input_type: "tel",
        },
        {
          name: "dBirth",
          label: "Date of Birth",
          placeholder: "mm/dd/yyyy",
          type: "input",
          required: true,
          input_type: "date",
        },
        {
          name: "occupation",
          label: "Occupation",
          placeholder: "",
          type: "input",
          required: false,
          input_type: "text",
        },
      ],
    },
    {
      section: "Addresses",
      infoTooltip:
        "Please list all the addresses where you lived during the tax year. Include any temporary or short-term residences if they were your primary home at any point.",
      fields: [
        {
          name: "addresses",
          placeholder: "Address",
          type: "multi_address",
          required: true,
        },
      ],
    },
  ],
};

export const BTF_SCHEMA = {
  Tax_Basics: [
    {
      section: "Filing_Basic",
      fields: [
        {
          name: "taxYears",
          label: "Year(s) for the filing",
          description: "Select all years you want to file for",
          type: "multi_select",
          option: "taxableYears",
          required: true,
        },
        {
          name: "filingStatus",
          label: "Business Type",
          type: "single_select",
          description: "Lets us know what type of business you are filing for",
          option: "businessFilingStatuses",
          required: true,
        },
        {
          name: "businessClosedMerged",
          label: "Business closed or merged?",
          type: "single_boolean_choice",
        },
      ],
    },
    {
      section: "State_Filing",
      infoTooltip:
        "Many states require an annual report; indicate how many you will file for this year.",
      fields: [
        {
          name: "stateAnnualReport",
          label: "Do you have a state annual report?",
          type: "multi_boolean_choice",
          fields: [
            {
              type: "numeric",
              name: "stateAnnualReportCount",
              label: "Enter the number of states you want to file for",
              inputType: "integer",
              defaultValue: 1,
              min: 0,
              max: 50,
            },
            {
              type: "multi_select",
              name: "stateAnnualReports",
              label: "(Optional) Select states you want to file for",
              description: "This is optional, but having the states will help us achieve a better match with the right expert & let the expert know which states you want to file for",
              option: "usaStates",
            },
          ],
        },
      ],
    },
    {
      section: "Assets_&_Depreciation",
      infoTooltip:
        "Include any significant asset purchases (equipment, vehicles, property) this year. Number helps estimate expert workload.",
      fields: [
        {
          name: "purchaseAssets",
          label: "Did you purchase any assets?",
          type: "multi_boolean_choice",
          fields: [
            {
              type: "numeric",
              name: "purchaseAssetsCount",
              label: "Number of assets?",
              description: "Enter the number of purchased assets. This helps your tax expert plan depreciation schedules.",
              inputType: "integer",
              defaultValue: 1,
            },
            {
              type: "multi_select",
              name: "puchasedAssetsType",
              label: "(Optional) Type of assets you purchased",
              description: "Select categories of assets purchased; expert will request detailed docs later.",
              option: "puchasedAssetsType",
            },
          ],
        },
      ],
    },
    {
      section: "Additional_Notes",
      fields: [
        {
          name: "description",
          label: "Anything else to add?",
          placeholder: "Anything else to add",
          type: "textarea",
        },
      ],
    },
  ],
  Background: [
    {
      section: "Basic_Business_Information",
      fields: [
        {
          name: "businessName",
          label: "Business name (legal name)",
          type: "input",
          required: true,
          input_type: "text",
        },
        {
          name: "yearEstablished",
          label: "Year Founded",
          type: "single_select",
          option: "businessFormedYear",
          required: true,
        },
        {
          name: "stateRegistration",
          label: "State of registration",
          type: "single_select",
          option: "usaStates",
          required: true,
          description: "State where the business was legally formed.",
        },
        {
          name: "stateForeign",
          label: "State of foreign registration",
          type: "multi_select",
          option: "usaStates",
          description: "States where the business has been registered, excluding the state of formation",
        },
      ],
    },
    {
      section: "Business_Address",
      fields: [
        {
          name: "businessAddress",
          placeholder: "Business address (state matters for state filing)",
          type: "single_address",
          required: true,
        },
      ],
    },
    {
      section: "Ownership_&_Structure",
      fields: [
        {
          name: "ownersNumber",
          label: "Number of owners or shareholders",
          type: "numeric",
          required: true,
          inputType: "integer",
          defaultValue: 1,
          min: 1,
        },
        {
          name: "ownershipChanges",
          label: "Any ownership changes during the year?",
          placeholder: "Any ownership changes during the year?",
          type: "single_boolean_choice",
        },
        {
          name: "distributionDividendsIssued",
          label: "Were any distributions/dividends issued?",
          placeholder: "Were any distributions/dividends issued?",
          type: "single_boolean_choice",
        },
      ],
    },
  ],
};
